import uuid from 'uuid';
import isAscii from 'validator/lib/isAscii';
import isLength from 'validator/lib/isLength';
import { getBuyingOptionsText } from '../project/subproject-model';

export const TEMPLATE_CAUSE_TYPE = {
    MEMORIAL: 'MEMORIAL',
    BIRTHDAY: 'BIRTHDAY',
    DUGNAD: 'DUGNAD',
    SCHOOL_PRIMARY_AND_KINDERGARTEN: 'SCHOOL_PRIMARY_AND_KINDERGARTEN',
    SCHOOL_VGS: 'SCHOOL_VGS',
    CHRISTMAS: 'CHRISTMAS',
};

export function getTemplateCausePicturePaths(templateCauseId) {
    const base = `uploads/template-cause/${templateCauseId}`;
    return {
        tmp: `${base}/tmp.jpg`,
        original: `${base}/original.jpg`,
        cropped1800: `${base}/template-cause-image-${uuid.v4()}.jpg`,
    };
}

export const TEMPLATE_PLACEHOLDER_TITLE = 'Start en spleis til din medlemsforening';
const TEMPLATE_PLACEHOLDER_IMAGE = 'default-cause-image.svg';
const DEFAULT_DUGNAD_IMAGE = 'default-dugnad-cause-image.svg';
const DEFAULT_BIRTHDAY_IMAGE = 'standardbilde-birthday.png';

export const errorsInTemplateCauseForActivation = ({ title, description, campaignId }) => {
    if (!title || errorsInTitle(title)) {
        return 'Tittelen må vœre mellom 3-100 tegn.';
    } else if (!description || errorsInDescription(description)) {
        return 'Beskrivelsen må vœre mellom 3-500 tegn.';
    } else if (campaignId && errorsInCampaignId(campaignId)) {
        return errorsInCampaignId(campaignId);
    }
    return '';
};

export const errorsInTitle = (title) => {
    if (!title || !isLength(title, { min: 3, max: 100 })) {
        return 'Tittelen må vœre mellom 3–100 tegn.';
    }
    return '';
};

export const errorsInDescription = (description) => {
    if (!description || !isLength(description, { min: 3, max: 250 })) {
        return 'Beskrivelsen må vœre mellom 3–250 tegn.';
    }
    return '';
};

export const errorsInCampaignId = (campaignId) => {
    if (!campaignId) {
        return '';
    } else if (!isLength(campaignId, { min: 1, max: 50 })) {
        return 'Kampanje-ID må vœre mellom 1–50 tegn.';
    } else if (!isAscii(campaignId)) {
        return 'Kampanje-ID kan bare inneholde tall, bokstaver og vanlige spesialtegn.';
    }
    return '';
};

export const getTemplateCauseImage = (cause) => {
    if (cause.img_url) {
        return cause.img_url;
    }
    if (cause.dugnad) {
        return DEFAULT_DUGNAD_IMAGE;
    }
    if (cause.birthday) {
        return DEFAULT_BIRTHDAY_IMAGE;
    }
    return TEMPLATE_PLACEHOLDER_IMAGE;
};
export const mapTemplateCauseToFrontend = (templateCause) => {
    if (!templateCause) {
        return undefined;
    }
    return {
        id: templateCause.id,
        created_at: templateCause.created_at,
        organization_id: templateCause.organization_id,
        title: templateCause.title,
        description: templateCause.description,
        active: templateCause.active,
        template_id: templateCause.template_id,
        img_url: templateCause.img_url,
        profile: templateCause.profile,
        position: templateCause.position,
        deleted: templateCause.deleted,
        project_id: templateCause.project_id,
        dugnad: templateCause.dugnad,
        birthday: templateCause.birthday,
        shortlink: templateCause.shortlink,
        schoolId: templateCause.school_id,
        type: templateCause.type,
        video_url: templateCause.video_url,
        video_loop: templateCause.video_loop,
        email_config: templateCause.email_config,
    };
};

export const mapOrganizationCauseToFrontend = (templateCause) => {
    if (!templateCause) {
        return undefined;
    }
    return {
        id: templateCause.cause_id,
        templateId: templateCause.template_id,
        title: templateCause.cause_title,
        image: templateCause.cause_image,
        projectCount: parseInt(templateCause.cause_project_count, 10),
        organizationName: templateCause.organization_name,
        organizationId: templateCause.organization_id,
        organizationImage: templateCause.organization_image,
    };
};

export const getTemplateCauseDugnadInviteText = ({
    productRewardCategories,
    organizationName,
    purpose,
    templateCauseLink,
}) => {
    const buyingOptionsText = getBuyingOptionsText({
        prefix: 'Dugnaden skal selge',
        rewards: productRewardCategories,
        postfix: '',
    });
    return `Hallo!

Nå er det dugnadstid, og jeg vil gjerne at du skal starte en dugnad til ${organizationName} via denne linken: ${templateCauseLink}

Alt av info er fylt inn fra før, så det er veldig enkelt å komme i gang, men du har mulighet til å gjøre innholdet mer personlig hvis du vil det. ${
        buyingOptionsText ? `${buyingOptionsText} og Spleis sender produktene direkte hjem til de som kjøper.` : ''
    }

Hvis det er flere som skal bidra er det lurt å invitere de som deltakere etter du har startet dugnaden, så får de sin egen salgsside knyttet til dugnaden som de kan dele.

${purpose?.length > 0 && purpose}

Lykke til med dugnaden!`;
};

const getPrimaryAndKindergartenInviteText = ({ organizationName, templateCauseLink }) => {
    return `Hei!

Nå er det mulig å melde seg på årets skoleinnsamling til inntekt for ${organizationName}.

Det er superlett å komme i gang ved å følge disse stegene:
1. Gå til ${templateCauseLink} for å starte en spleis
2. Søk opp din skole, og du er i gang! Alt av info er fylt inn fra før, men det er mulig å sette sitt eget preg på innholdet om en ønsker det
3. Del spleisen med lærere, foreldre, og andre som kan tenke seg å bidra til deres skoleinnsamling

Tusen takk for deres bidrag, og lykke til med innsamlingen 🤩`;
};

const getVgsInviteText = ({ organizationName, templateCauseLink }) => {
    return `Hei!

Nå er skoleaksjonen til inntekt for ${organizationName} i gang 🎉 Elever eller hele klasser kan enkelt starte sine egne spleiser ved å følge stegene under.

Slik starter du en spleis:
1. Gå til ${templateCauseLink} for å starte en spleis
2. Fyll inn hvilken klasse du tilhører, og du er i gang! Alt av info er fylt inn fra før, men det er mulig å sette sitt eget preg på innholdet om en ønsker det
3. Del spleisen med familie, venner, og andre som kan tenke seg å bidra til din skoleinnsamling

Tusen takk for deres bidrag, og lykke til med innsamlingen 🤩`;
};

export const getDefaultStartProjectText = (causeType) => {
    switch (causeType) {
        case TEMPLATE_CAUSE_TYPE.MEMORIAL:
            return 'Start en minnespleis';
        case TEMPLATE_CAUSE_TYPE.BIRTHDAY:
            return 'Start en bursdagsinnsamling';
        default:
            return 'Start en spleis for aksjonen';
    }
};

export const getInviteOptions = ({
    templateCauseType,
    productRewardCategories,
    organizationName,
    templateCauseLink,
    purpose,
}) => {
    switch (templateCauseType) {
        case TEMPLATE_CAUSE_TYPE.DUGNAD:
            return {
                copyText: getTemplateCauseDugnadInviteText({
                    productRewardCategories,
                    organizationName,
                    templateCauseLink,
                    purpose,
                }),
                buttonText: 'Inviter',
                titleText: 'Inviter til aksjonen',
            };
        case TEMPLATE_CAUSE_TYPE.SCHOOL_PRIMARY_AND_KINDERGARTEN:
            return {
                copyText: getPrimaryAndKindergartenInviteText({ organizationName, templateCauseLink }),
                buttonText: 'Instruks til skolene',
                titleText: 'Instruks til skolene',
                copyTextFieldLabel: 'Instruks',
                introTextArray: [
                    'Her er en oppskrift du kan dele med skolene eller barnehagene som skal delta på skoleinnsamlingen.',
                    'Teksten kan du så klart tilpasse som du vil, men dette kan være et fint utgangspunkt.',
                ],
            };
        case TEMPLATE_CAUSE_TYPE.SCHOOL_VGS:
            return {
                copyText: getVgsInviteText({ organizationName, templateCauseLink }),
                buttonText: 'Instruks til skolen',
                titleText: 'Instruks til skolen',
                copyTextFieldLabel: 'Instruks',
                introTextArray: [
                    'Her er en oppskrift du kan dele med skolen som skal ha skoleinnsamlingen.',
                    'Teksten kan du så klart tilpasse som du vil, men dette kan være et fint utgangspunkt.',
                ],
            };
        default:
            return;
    }
};

export function getTemplateCauseEmailConfigLogoPicturePath({ templateCauseId, organizationId, imageType }) {
    return `uploads/organization/${organizationId}/causes/${templateCauseId}/custom-email-config/${imageType}/custom-email-config-image-${uuid.v4()}.png`;
}

export const isCauseEmailConfigEnabledAndHasInfo = ({ templateCause }) => {
    if (!templateCause) {
        return false;
    }
    return (
        templateCause.email_config?.enabled &&
        ((typeof templateCause.email_config?.text === 'string' && templateCause.email_config?.text.length > 0) ||
            (typeof templateCause.email_config?.heading === 'string' && templateCause.email_config?.heading.length > 0))
    );
};
